import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import { RiArrowDownSLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import type { BuzonCategoriaTribunal } from '@/shared/types/interfaces/BuzonJudicial.interface';

interface CategoriaTribunalSelectProps {
  options: BuzonCategoriaTribunal[];
  value: BuzonCategoriaTribunal[];
  onChange: (value: BuzonCategoriaTribunal[]) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: string;
  noOptionsText?: string;
}

/**
 * Selector múltiple de "Categoría de tribunal" (instancia) — el usuario puede marcar una o varias.
 * El label vive como texto simple ARRIBA del campo (no como legend flotante sobre el borde, patrón
 * de Material antiguo). Reutilizado tanto para el caso de 1 solo NUC como en cada fila de la lista
 * multi-caso.
 */
const CategoriaTribunalSelect = ({
  options,
  value,
  onChange,
  disabled,
  size = 'small',
  label,
  noOptionsText,
}: CategoriaTribunalSelectProps) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#6b7280' }}>
        {label ?? t('buzonJudicial.enableModal.categoriaTribunalLabel', 'Categoría de tribunal')}
      </Typography>
      <Autocomplete
        multiple
        options={options}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        getOptionLabel={(option) => option.categoriaTribunal}
        isOptionEqualToValue={(option, selected) => option.idCategoriaTribunal === selected.idCategoriaTribunal}
        disabled={disabled}
        size={size}
        popupIcon={<RiArrowDownSLine size={18} />}
        noOptionsText={noOptionsText ?? t('buzonJudicial.enableModal.noNewCategoriesAvailable', 'No hay instancias disponibles')}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...chipProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option.categoriaTribunal}
                size="small"
                {...chipProps}
                sx={{
                  backgroundColor: '#D4EFFF',
                  color: '#0843A0',
                  fontWeight: 600,
                  '& .MuiChip-deleteIcon': { color: '#0843A0', opacity: 0.55, '&:hover': { opacity: 1 } },
                }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value.length === 0 ? t('buzonJudicial.enableModal.categoriaTribunalPlaceholder', 'Selecciona una categoría') : undefined}
          />
        )}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#f8fafc',
            borderRadius: '10px',
            '& fieldset': { borderColor: '#d1d5db' },
            '&:hover fieldset': { borderColor: '#9ca3af' },
            '&.Mui-focused fieldset': { borderColor: '#0050DA', borderWidth: '1.5px' },
          },
          '& .MuiOutlinedInput-root.Mui-disabled': {
            backgroundColor: '#f1f5f9',
          },
        }}
      />
    </Box>
  );
};

export default CategoriaTribunalSelect;
